/**
 * Hive Component: Call Handler
 * -------------------------------------------------------------
 * @copyright (c) 2024 [Paul Höcker|PHMG](https://phmg.online). All rights reserved.
 * @license   GNU General Public License version 3 or later
 * @version   1.0.0, 2024-01-01 00:00
 */

import Handler from "./Handler.js";

/**
 * Constants
 */

const NAME = 'CallHandler'
const VERSION = '1.3.0'

export default class CallHandler extends Handler {

	/**
	 * @inheritDoc
	 */
	static NAME = NAME

	/**
	 * @inheritDoc
	 */
	static VERSION = VERSION

	Defaults = { silentResponse : true, currURL : '' }

	initiated = false;
	calling = false;

	baseURL = '';
	defaultCallOptions = { showMessage : true, requestId : "" }

	Start = null;
	End = null;
	Queue = [];

	/**
	 * Attach to a controller class
	 * @param controller
	 * @param {boolean|string|{silent:{boolean},url:{string}}} [options]
	 */
	constructor(controller, options) {
		super(controller, options);
	}

	/**
	 * @inheritDoc
	 */
	parseOptions(options) {
		if (typeof options === 'boolean') {
			return { silentResponse : options };
		}

		if (typeof options === 'string') {
			return { currURL : options };
		}

		options = options || {};
		let parsed = {};
		if (options.hasOwnProperty('silent')) {
			parsed.silentResponse = options['silent'];
		}
		if (options.hasOwnProperty('url')) {
			parsed.currURL = options['url'];
		}

		return super.parseOptions(parsed);
	}

	/**
	 * Set the current url to call
	 * @param {string} URL
	 * @returns {CallHandler}
	 */
	url(URL) {
		this.set({ currURL : URL });

		return this;
	}

	/**
	 * Check if handler is busy / calling
	 * @return {boolean}
	 */
	isBusy() {
		return this.calling;
	}

	/**
	 * Make a GET request
	 * @param {string} task
	 * @param {*} data
	 * @param {function} callback
	 * @param {Object|boolean} [optionsOrSilent] Options / userdata object for this call. If boolean sets no message option.
	 */
	getRequest(task, data, callback, optionsOrSilent) {

		// Prepare options, boolean for backwards compatibility (noMessage)
		if (typeof optionsOrSilent === 'boolean') optionsOrSilent = { showMessage : !optionsOrSilent };
		let options = Object.assign({}, this.defaultCallOptions, optionsOrSilent || {});

		// Check if we are already calling
		if (!this._beforeRequest({ method : 'get', task : task, data : data, callback : callback }, options)) {
			return;
		}

		// Prepare the data
		const requestData = data || {};
		const token = this.utils.getToken();
		requestData['task'] = task;
		requestData[token] = '1';

		this.#get(this.get('currURL'), requestData)
			.catch((error) => {
				this.utils.odb(error, this);

				let result = this._afterRequest({ success : false, data : null, message : error }, options);

				// if (!noMessage && (this.get('silentResponse', false) || this.utils.error(error, null, 5))) return;
				callback(result.success, result.data, result.message);
			})
			.then((response) => {
				this.utils.odb(response, this);

				let result = this._afterRequest(response, options);
				// response = this.process(response, noMessage);
				callback(result.success, result.data, result.message);
			});
	}

	/**
	 * Make a POST request
	 * @param {string} task
	 * @param {*} data
	 * @param {function} [callback]
	 * @param {Object|boolean} [options] Options / userdata object for this call, boolean for backwards compatibility (noMessage)
	 * */
	postRequest(task, data, callback, options) {

		// Prepare options
		if (typeof options === 'boolean') options = { showMessage : !options };
		options = Object.assign({}, this.defaultCallOptions, options);

		// Check if we are already calling
		if (!this._beforeRequest({ method : 'post', task : task, data : data, callback : callback }, options)) {
			return;
		}

		// Prepare the data
		data = data || {};
		let token = this.utils.getToken();
		if (data instanceof FormData) {
			data.set('task', task);
			data.set(token, '1');
		} else {
			data['task'] = task;
			data[token] = '1';
		}

		this.#post(this.get('currURL'), data)
			.catch((error) => {
				this.utils.odb(error, this);

				let result = this._afterRequest({ success : false, data : null, message : error }, options);

				callback(result.success, result.data, result.message);
			})
			.then((response) => {
				this.utils.odb(response, this);

				let result = this._afterRequest(response, options);

				// response = this.process(response, noMessage);
				callback(result.success, result.data, result.message);
			});
	}

	/**
	 * Make a basic request, skipping processing
	 * @param {string} url
	 * @param {object|null} [params]
	 * @param {object|null} [options]
	 * @param {function} [callback]
	 */
	request(url, params, options, callback) {
		params = params || null;
		options = Object.assign({
			method         : 'GET', // *GET, POST, PUT, DELETE, etc.
			mode           : 'cors', // no-cors, *cors, same-origin
			cache          : 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
			credentials    : 'same-origin', // include, *same-origin, omit
			redirect       : 'follow', // manual, *follow, error
			referrerPolicy : 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
		}, options || {})

		if (options.method === 'GET') {
			if (params) url += '&' + this.serialize(params);
		}

		fetch(url, options)
			.catch((error) => {
				this.utils.odb(error, this)
				if (typeof callback === 'function') {
					callback(false, error);
				}
			})
			.then((response) => {
				this.utils.odb(response, this);

				let jsonResponse = response.json();
				if (typeof callback === 'function') {
					callback(true, jsonResponse);
				}
			})
	}

	/**
	 * Send a request without processing
	 * @param {string} task
	 * @param {null|object} [data]
	 * @param {string} [method]
	 */
	sendRequest(task, data, method) {
		let requestData = data || null;
		const token = this.utils.getToken();

		let init = {
			method         : method || 'GET', // *GET, POST, PUT, DELETE, etc.
			mode           : 'cors', // no-cors, *cors, same-origin
			cache          : 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
			credentials    : 'same-origin', // include, *same-origin, omit
			redirect       : 'follow', // manual, *follow, error
			referrerPolicy : 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
		}

		let url = this.get('currURL');
		if (init.method === 'GET') {
			url += '&' + this.serialize(requestData);
		} else {
			if (requestData instanceof FormData) {
				requestData.set('task', task);
				requestData.set(token, '1');
			} else {
				requestData['task'] = task;
				requestData[token] = '1';
				requestData = JSON.stringify(requestData);
				init.headers = { 'Content-Type' : 'application/json' };
			}
			init.body = requestData // body data type must match "Content-Type" header
		}

		fetch(url, init)
			.then(response => response.json())
			.catch(error => this.utils.odb(error, this))
		;
	}

	/**
	 * Runs before each call
	 * @param {Object} requestData
	 * @param {Object} [options]
	 * @returns {boolean}
	 * @private
	 */
	_beforeRequest(requestData, options) {
		options = options || this.defaultCallOptions;
		if (this.calling) {
			!options.showMessage ?
				console.log('Ongoing call...') :
				this.utils.info('GLOBAL_INFO_PLEASE_WAIT', null, 3);

			// If a requestId has been set, remove all others with this id
			if (options.requestId && this.Queue.length) {
				this.Queue = this.Queue.filter(data => !data.options.requestId || data.options.requestId !== options.requestId)
			}

			requestData.options = options;
			this.Queue.push(requestData);

			return false;
		}

		this.calling = true;

		return true;
	}

	/**
	 * Runs after each call
	 * @param {Object} responseData
	 * @param {Object} [options]
	 * @returns {{success:boolean,data:*,message:string}} result
	 * @private
	 */
	_afterRequest(responseData, options) {
		this.calling = false;

		let success = responseData.success || false,
			message = responseData.message || responseData.errors || '',
			data = responseData.data || null;
		options = options || this.defaultCallOptions;

		// Display error message
		if (!success) {
			message = message || 'ERROR';
			options.showMessage && (this.get('silentResponse', false) || this.utils.error(message, data, 5));

			return { success : false, data : data, message : message };
		}

		// Update the token
		if (data !== null) {
			let token = data.token || '';
			if (token && token.length) {
				this.utils.setToken(token);
			}
		}

		// Display success message
		if (message && message.length && message !== 'ok') {
			options.showMessage && (this.get('silentResponse', false) || this.utils.success(message, data, 5));
		}

		// Get next in line
		if (this.Queue.length) {
			let requestData = this.Queue.shift();
			if (requestData.method === 'get') {
				this.getRequest(requestData.task, requestData.data, requestData.callback, requestData.options);
			} else {
				this.postRequest(requestData.task, requestData.data, requestData.callback, requestData.options);
			}
		}

		return { success : true, data : data, message : message };
	}

	/**
	 * Process the response (DEPRECATED, IGNORE)
	 * @param response
	 * @param {boolean} [noMessage]
	 * @returns {{success:boolean, message:string, data:*}}
	 * @deprecated
	 */
	deprecatedProcess(response = {}, noMessage = false) {
		let success = response.success || false,
			message = response.message || response.errors || '',
			data = response.data || null;

		// Display error message
		if (!success) {
			message = message || 'ERROR';
			!noMessage && (this.get('silentResponse', false) || this.utils.error(message, data, 5));

			return { success : false, data : data, message : message };
		}

		// Update the token
		if (data !== null) {
			let token = data.token || '';
			if (token && token.length) {
				this.utils.setToken(token);
			}
		}

		// Display success message
		if (message && message.length && message !== 'ok') {
			!noMessage && (this.get('silentResponse', false) || this.utils.success(message, data, 5));
		}

		return { success : true, data : data, message : message };
	}

	/**
	 * Serialize a request object
	 * @param data
	 * @param prefix
	 * @returns {string}
	 */
	serialize(data, prefix = '') {
		let str = [], p;
		for (p in data) {
			if (data.hasOwnProperty(p)) {
				let k = prefix ? prefix + "[" + p + "]" : p,
					v = data[p];
				str.push((v !== null && typeof v === "object") ?
					this.serialize(v, k) :
					encodeURIComponent(k) + "=" + encodeURIComponent(v));
			}
		}

		return str.join("&");
	}

	/**
	 * Next try to convert an object to form data
	 * @param data
	 */
	toFormData(data) {
		data = data || {};
		const flat = {};
		this.flatten(flat, data);
		const formData = new FormData();
		for (let key in flat) {
			if (flat.hasOwnProperty(key)) {
				formData.append(key, flat[key]);
				/*Array.isArray(data[key]) ?
					data[key].forEach(value => formData.append(key + '[]', value)) :
					formData.append(key, data[key]);*/
			}
		}

		return formData;
	}

	/**
	 *
	 * @param {{}} flat
	 * @param {*} data
	 * @param {string} [keys]
	 * @param {number} [level]
	 */
	flatten(flat, data, keys = '', level = 0) {
		if (this.utils.isObject(data)) {
			for (let key in data) {
				if (data.hasOwnProperty(key)) {
					let k = keys + (level ? '[' + key + ']' : key);
					let value = data[key];
					this.flatten(flat, value, k, level + 1)
				}
			}
		} else if (Array.isArray(data)) {
			for (let value of data) {
				let k = keys += '[]';
				this.flatten(flat, value, k, level + 1);
			}
		} else {
			flat[keys] = data;
		}
	}

	/**
	 *
	 * @param {object} data
	 * @param {object} defaults
	 */
	prepareData(data, defaults = null) {
		let filtered = this.utils.filter(data, ['post', 'get']);
		if (!defaults || typeof defaults !== 'object') {
			return filtered;
		}

		return Object.assign({}, defaults, filtered);
	}

	/**
	 * Send get data
	 * @param {string} url
	 * @param {object|null} [data]
	 * @returns {Promise<Response>}
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#supplying_request_options
	 */
	async #get(url = '', data = {}) {
		if (data) url += '&' + this.serialize(data)

		// Default options are marked with *
		const response = await fetch(url, {
			method         : 'GET', // *GET, POST, PUT, DELETE, etc.
			mode           : 'cors', // no-cors, *cors, same-origin
			cache          : 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
			credentials    : 'same-origin', // include, *same-origin, omit
			redirect       : 'follow', // manual, *follow, error
			referrerPolicy : 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
		});

		return response.json();
	}

	/**
	 * Send post data
	 * @param {string} url
	 * @param {object|string} data
	 * @returns {Promise<any>}
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#supplying_request_options
	 */
	async #post(url = '', data = {}) {
		// Default options are marked with
		// noinspection JSCheckFunctionSignatures
		const response = await fetch(url, {
			method         : 'POST', // *GET, POST, PUT, DELETE, etc.
			mode           : 'cors', // no-cors, *cors, same-origin
			cache          : 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
			credentials    : 'same-origin', // include, *same-origin, omit
			redirect       : 'follow', // manual, *follow, error
			referrerPolicy : 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
			body           : data // body data type must match "Content-Type" header
			/*body           : JSON.stringify( data ) // body data type must match "Content-Type" header*/
			/* headers        : headers,*/
		});

		return response.json();
	}


}